import { AxiosError } from 'axios'

/** Formato do codigo curto do erro interno, como prefixo da mensagem. */
const PREFIXO_DE_CODIGO = /^\[([0-9A-Z]{4}-[0-9A-Z]{4}-[0-9A-Z]{2})\]\s*/

/**
 * Extrai o código curto do erro interno (ex.: ABC3-K7Q2-M9), quando o backend mandou.
 *
 * O backend manda de duas formas, porque os handlers têm formatos de corpo diferentes:
 * o handler genérico devolve um objeto com o campo `codigo`, e os handlers que devolvem
 * lista trazem o código como prefixo `[XXXX-XXXX-XX]` na mensagem.
 *
 * É esse código que o cliente informa ao Suporte, e é por ele que o Suporte acha a
 * ocorrência exata no banco. Sem ele na tela, o cliente não tem o que informar.
 */
export const codigoDoErro = (error: AxiosError<any>): string => {
	const data: any = error.response?.data
	if (!data) return ''
	if (typeof data.codigo === 'string' && data.codigo) return data.codigo

	const texto = Array.isArray(data) ? data[0] : data.message
	if (typeof texto !== 'string') return ''
	const achado = texto.match(PREFIXO_DE_CODIGO)
	return achado ? achado[1] : ''
}

/**
 * Remove o prefixo `[XXXX-XXXX-XX]` da mensagem.
 *
 * O prefixo existe apenas como veículo para o código chegar dos handlers que devolvem lista. Uma
 * vez que o código é exibido no seu próprio campo, repeti-lo colado na mensagem só atrapalha a
 * leitura.
 */
export const semPrefixoDeCodigo = (texto: string): string =>
	typeof texto === 'string' ? texto.replace(PREFIXO_DE_CODIGO, '').trim() : texto


/** Acrescenta o código do erro ao texto fixo, quando existe. */
const comCodigo = (texto: string, error: AxiosError<any>): string => {
	const codigo = codigoDoErro(error)
	return codigo ? `${texto}. Código do erro: ${codigo}` : texto
}

/**
 * O inverso de `comCodigo`: tira o "Código do erro: XXXX-XXXX-XX" do fim da frase.
 *
 * Serve à lista de "Erros de hoje", onde o código já aparece em destaque na própria linha. O texto
 * guardado continua inteiro; quem some com o código é só a exibição resumida, para não mostrar a
 * mesma informação duas vezes na mesma linha.
 *
 * O aviso da tela NÃO usa isto: ali o texto continua como sempre foi, com o código no fim da frase.
 */
export const semSufixoDeCodigo = (texto: string): string =>
	typeof texto === 'string'
		? texto.replace(/\.?\s*Código do erro:\s*\S+\s*$/, '').trim()
		: texto

/**
 * Relança o erro de uma requisição `responseType: 'arraybuffer'` com o corpo já decodificado.
 *
 * Quem pede arraybuffer (impressão, download) recebe o corpo do erro como ArrayBuffer, e não como
 * objeto: o axios respeita o `responseType` também na resposta de erro. Sem decodificar, nada da
 * resposta é legível.
 *
 * O ponto de atenção é COMO relançar. O padrão anterior era `throw new Error(corpoDecodificado)`, e
 * isso destruía duas coisas: um `Error` comum não tem `response`, então o status desaparecia e com
 * ele o texto por status e o `codigoDoErro`; e `new Error(objeto)` produz a mensagem literal
 * "[object Object]", porque o construtor chama `String()` no argumento. O handler genérico de 500 é
 * justamente o que devolve objeto (`{ message, codigo, stack }`), então o caso que perdia tudo era
 * exatamente o erro interno, que é o único que tem código para o cliente informar ao Suporte. Só os
 * handlers que devolvem lista escapavam, porque `String(['a'])` dá `'a'` por acaso.
 *
 * Aqui o corpo decodificado volta para `error.response.data` e o erro relançado continua sendo o do
 * axios, com `status` no lugar. Assim `getRequestErrorMessage` e `codigoDoErro` funcionam como em
 * qualquer outra rota, sem nenhum tratamento especial para impressão.
 *
 * Relança SEMPRE, inclusive quando não há resposta (rede fora, timeout) ou quando o corpo não é
 * JSON (HTML de proxy, PDF truncado): engolir o erro devolveria `undefined` no lugar do arquivo e o
 * estouro apareceria depois, em quem tenta imprimir, longe da causa.
 */
export const relancarErroDeArquivo = (error: any): never => {
	const corpo = error?.response?.data
	if (corpo) {
		try {
			error.response.data = JSON.parse(new TextDecoder('utf-8').decode(corpo))
			// Corpo que não é JSON continua como veio: decodificar é uma melhoria, não um requisito.
			// eslint-disable-next-line no-empty
		} catch { }
	}
	throw error
}

export const getRequestErrorMessage = (prefixo: string | null, error: AxiosError<{ message?: string | string[] }>): string => {
	switch (error.response?.status) {
		case 401:
			return 'Recurso apenas para usuários autenticados'
		case 403:
			return 'Não tem permissão'
		case 405:
			return 'Método não permitido'
		case 500:
		case 503:
			return comCodigo('Ocorreu um erro interno, contate o suporte', error)
		case 504:
			return 'Tempo limite excedido'
		case 502:
			return comCodigo('O sistema está indisponível, aguarde um instante e tente novamente', error)
	}
	if (error.response && error.response.data) {
		const message = error.response.data.message
		if (!message) return (prefixo || '') + error.response.data.toString()
		return Array.isArray(message)
			? (prefixo || '') + message.join('; ')
			: (prefixo || '') + message
	}
	return (prefixo || '') + error.message
}


export const getRequestError = (error: AxiosError<{ message?: string | string[] }>): string => {
	switch (error.response?.status) {
		case 401:
			return 'Recurso apenas para usuários autenticados'
		case 403:
			return 'Não tem permissão'
		case 405:
			return 'Método não permitido'
		case 500:
		case 503:
			return comCodigo('Ocorreu um erro interno, contate o suporte', error)
		case 504:
			return 'Tempo limite excedido'
		case 502:
			return comCodigo('O sistema está indisponível, aguarde um instante e tente novamente', error)
	}
	if (error.response && error.response.data) {
		const message = error.response.data.message
		if (!message) return error.response.data.toString()
		return Array.isArray(message)
			? message.join('; ')
			: message
	}
	return error.message
}